/**
 * KeyboardShortcutsModal - Phase 7
 * 
 * Displays available keyboard shortcuts in a modal overlay.
 * Triggered by pressing '?' key.
 * 
 * @module KeyboardShortcutsModal
 */

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Keyboard } from 'lucide-react';
import { KEYBOARD_SHORTCUTS } from '@/hooks/useKeyboardShortcuts';

// ============================================================================
// SHORTCUT KEY DISPLAY
// ============================================================================

function ShortcutKey({ children }) {
  return (
    <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 text-xs font-mono font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded shadow-sm">
      {children}
    </kbd>
  );
}

// ============================================================================
// SHORTCUT ROW
// ============================================================================

function ShortcutRow({ shortcut }) {
  return (
    <div className="flex items-center justify-between py-1.5">
      <span className="text-sm text-gray-600">{shortcut.description}</span>
      <ShortcutKey>{shortcut.key === 'Escape' ? 'Esc' : shortcut.key.toUpperCase()}</ShortcutKey>
    </div>
  );
}

// ============================================================================
// SHORTCUT CATEGORY
// ============================================================================

function ShortcutCategory({ category, shortcuts }) {
  return (
    <div className="mb-4 last:mb-0">
      <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
        {category}
      </h4>
      <div className="space-y-0.5">
        {shortcuts.map((shortcut, index) => (
          <ShortcutRow key={index} shortcut={shortcut} />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function KeyboardShortcutsModal({ isOpen, onClose }) {
  // Group shortcuts by category
  const groupedShortcuts = Object.values(KEYBOARD_SHORTCUTS).reduce((acc, shortcut) => {
    const category = shortcut.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(shortcut);
    return acc;
  }, {});

  // Order categories
  const categoryOrder = ['Actions', 'Navigation', 'Filters', 'Modes', 'Bulk Actions', 'Help'];

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[100]"
          />
          
          {/* Modal Container - centers the modal */}
          <div className="fixed inset-0 z-[101] flex items-center justify-center p-4 pointer-events-none">
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl max-h-[80vh] overflow-hidden pointer-events-auto"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#006D77]/10 rounded-xl flex items-center justify-center">
                    <Keyboard className="w-5 h-5 text-[#006D77]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Keyboard Shortcuts</h3>
                    <p className="text-xs text-gray-500">Quick actions for power users</p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                >
                  <X size={20} />
                </button>
              </div>
            
            {/* Content */}
            <div className="px-6 py-4 overflow-y-auto max-h-[calc(80vh-100px)]">
              <div className="grid grid-cols-2 gap-6">
                {categoryOrder.map(category => {
                  const shortcuts = groupedShortcuts[category];
                  if (!shortcuts || shortcuts.length === 0) return null;
                  
                  return (
                    <ShortcutCategory
                      key={category}
                      category={category}
                      shortcuts={shortcuts}
                    />
                  );
                })}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-3 bg-gray-50 border-t border-gray-100 text-center">
              <p className="text-xs text-gray-500">
                Press <ShortcutKey>Esc</ShortcutKey> to close
              </p>
            </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
}
